import type ExcelJSNamespace from "exceljs";
import { beginDataExport } from "@/lib/data-export-events";
import {
	attemptDynamicImportRecovery,
	describeDynamicImportError,
	loadExcelJs,
} from "@/lib/dynamic-import";

/** ARGB constants for Excel styling (CSS vars not available in canvas/export context). */
const EXCEL_HEADER_ARGB = "FF165C3A"; // brand primary — hsl(148 57% 22%) = #165C3A
const EXCEL_SUMMARY_ARGB = "FFE4F1EA"; // light green summary row
/** Kolorowanie wg znaku (kolumny `signColored`): dodatnie = korzysc, ujemne = strata.
 *  Nasycone celowo — pastele gina przy przegladaniu kilkuset wierszy i klient
 *  zglosil, ze sa nieczytelne. Oba tla trzymaja kontrast dla domyslnego czarnego
 *  tekstu, wiec nie trzeba zmieniac koloru czcionki. */
const EXCEL_SIGN_GOOD_ARGB = "FF8BC34A";
const EXCEL_SIGN_BAD_ARGB = "FFEF9A9A";

/** Excel ogranicza nazwy arkuszy do 31 znakow — dluzsze obcinamy zamiast rzucac. */
const EXCEL_SHEET_NAME_MAX = 31;

export class ExcelExportError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
	) {
		super(message);
		this.name = "ExcelExportError";
	}
}

export interface ExcelColumn {
	id: string;
	header: string;
	widthPx: number;
	isNumeric: boolean;
	/** Tlo komorki wg znaku liczby: dodatnia = zielone (korzysc), ujemna = czerwone. */
	signColored?: boolean;
	/** Format liczby kolumny `isNumeric` (domyslnie `#,##0.00`), np. `0` dla licznosci. */
	numFmt?: string;
	/**
	 * Wartosc w punktach procentowych (15.58 = 15,58 %). Komorka dostaje ulamek
	 * (0.1558) z formatem `0.00%` — klient przeliczal te kolumny recznie, zeby
	 * Excel liczyl na nich jak na procencie. Kolumna musi byc `isNumeric`.
	 */
	percent?: boolean;
}

const EXCEL_PERCENT_FMT = "0.00%";

function excelNumber(col: ExcelColumn, value: number): number {
	return col.percent ? value / 100 : value;
}

/**
 * Pojedynczy arkusz w workbooku multi-sheet. Tej samej semantyki uzywa
 * `ExcelExportOptions` w trybie single-sheet — multi-sheet to po prostu
 * tablica `ExcelSheet`.
 */
export interface ExcelSheet {
	name: string;
	columns: ExcelColumn[];
	rows: Record<string, unknown>[];
	summaryRow?: Record<string, unknown> | null;
	summaryLabel?: string;
	summaryLabelColumnId?: string;
	/** Podsumowanie w wierszu 1 (nad naglowkiem) zamiast pod ostatnim wierszem. */
	summaryOnTop?: boolean;
}

export interface ExcelExportOptions {
	// Single-sheet (legacy, BC) — gdy `sheets` jest puste lub nieobecne, te pola
	// definiuja jedyny arkusz.
	columns?: ExcelColumn[];
	rows?: Record<string, unknown>[];
	summaryRow?: Record<string, unknown> | null;
	summaryLabel?: string;
	summaryLabelColumnId?: string;
	summaryOnTop?: boolean;

	// Common.
	fileName?: string;

	// Multi-sheet — gdy obecne i niepuste, nadpisuje single-sheet shape.
	sheets?: ExcelSheet[];
}

type Worksheet = ExcelJSNamespace.Worksheet;

/**
 * Wstrzykuje kolumny, naglowek, wiersze i opcjonalny summary row do workbookowego
 * arkusza. DRY pomiedzy single-sheet (legacy) a multi-sheet (Task 10.5) sciezka.
 */
function styleNumericCells(row: ExcelJSNamespace.Row, columns: ExcelColumn[]): void {
	for (const [i, col] of columns.entries()) {
		if (!col.isNumeric) continue;
		const cell = row.getCell(i + 1);
		cell.numFmt = col.numFmt ?? (col.percent ? EXCEL_PERCENT_FMT : "#,##0.00");
		if (!col.signColored) continue;
		const v = cell.value;
		if (typeof v !== "number" || v === 0) continue;
		cell.fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: v > 0 ? EXCEL_SIGN_GOOD_ARGB : EXCEL_SIGN_BAD_ARGB },
		};
	}
}

function populateWorksheet(ws: Worksheet, sheet: ExcelSheet): void {
	const {
		columns,
		rows,
		summaryRow,
		summaryLabel = "SUMA",
		summaryLabelColumnId,
		summaryOnTop = false,
	} = sheet;
	// Stopka na gorze przesuwa naglowek do wiersza 2 (insertRow shiftuje reszte).
	const onTop = summaryOnTop && !!summaryRow;

	ws.columns = columns.map((col) => ({
		header: col.header,
		key: col.id,
		width: Math.max(8, Math.round(col.widthPx / 7)),
	}));

	for (const rowData of rows) {
		const values = columns.map((col) => {
			const val = rowData[col.id];
			if (col.isNumeric && typeof val === "number") return excelNumber(col, val);
			if (val === undefined || val === null) return "";
			return String(val);
		});
		styleNumericCells(ws.addRow(values), columns);
	}

	if (summaryRow) {
		const summaryValues = columns.map((col) => {
			if (col.id === summaryLabelColumnId) return summaryLabel;
			const val = summaryRow[col.id];
			if (col.isNumeric && typeof val === "number") return excelNumber(col, val);
			return "";
		});
		const sumRow = onTop ? ws.insertRow(1, summaryValues, "n") : ws.addRow(summaryValues);
		sumRow.font = { bold: true, size: 10 };
		sumRow.fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: EXCEL_SUMMARY_ARGB },
		};
		styleNumericCells(sumRow, columns);
	}

	// Naglowek stylujemy PO ewentualnym insercie — wtedy jest juz w wierszu 2.
	const headerRowNo = onTop ? 2 : 1;
	const headerRow = ws.getRow(headerRowNo);
	headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
	headerRow.fill = {
		type: "pattern",
		pattern: "solid",
		fgColor: { argb: EXCEL_HEADER_ARGB },
	};
	headerRow.alignment = { vertical: "middle" };
	headerRow.height = 20;

	ws.views = [{ state: "frozen", ySplit: headerRowNo }];
	ws.autoFilter = {
		from: { row: headerRowNo, column: 1 },
		to: { row: headerRowNo, column: columns.length },
	};
}

export async function exportToExcel(opts: ExcelExportOptions): Promise<void> {
	const finishExport = beginDataExport();
	try {
		// Lazy chunk via loadExcelJs — stale-chunk failures (Sentry 116556881
		// klasa) zostaną opisane przez describeDynamicImportError w catch
		// poniżej, zamiast wyciekać jako surowy TypeError.
		const ExcelJS = await loadExcelJs();

		const { fileName = "eksport" } = opts;

		const workbook = new ExcelJS.Workbook();
		workbook.creator = "Farmaster";

		const useMultiSheet = Array.isArray(opts.sheets) && opts.sheets.length > 0;

		if (useMultiSheet) {
			// `useMultiSheet` gwarantuje obecnosc i niepustosc opts.sheets.
			for (const sheet of opts.sheets as ExcelSheet[]) {
				const safeName = sheet.name.slice(0, EXCEL_SHEET_NAME_MAX);
				const ws = workbook.addWorksheet(safeName);
				populateWorksheet(ws, sheet);
			}
		} else {
			// Single-sheet legacy path — brak `sheets` lub pusta tablica + obecne
			// `columns/rows` (callerzy sprzed Task 10.5 nie ustawiaja `sheets`).
			if (!opts.columns || !opts.rows) {
				throw new ExcelExportError("exportToExcel: bez `sheets[]` wymagane sa `columns` i `rows`");
			}
			const ws = workbook.addWorksheet("Eksport");
			populateWorksheet(ws, {
				name: "Eksport",
				columns: opts.columns,
				rows: opts.rows,
				summaryRow: opts.summaryRow,
				summaryLabel: opts.summaryLabel,
				summaryLabelColumnId: opts.summaryLabelColumnId,
				summaryOnTop: opts.summaryOnTop,
			});
		}

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${fileName}.xlsx`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
		finishExport();
	} catch (err) {
		if (err instanceof ExcelExportError) throw err;
		// Best-effort: if the chunk import failed because the page is stale
		// after a deploy, schedule a one-shot reload. Reload is non-blocking,
		// the throw still happens — the browser cancels the in-flight work.
		attemptDynamicImportRecovery(err);
		throw new ExcelExportError(
			describeDynamicImportError(err, "Nie udało się wygenerować pliku Excel"),
			err,
		);
	}
}
